<template>
  <ag-grid-vue
    v-bind="agInvoices"
    style=" height: 65vh;"
    class="ag-theme-alpine"
    @gridReady="onGridReady"
    @cell-clicked="onCellClicked"
    @row-double-clicked="onRowDoubleClicked"
  />
</template>

<script>
/* eslint-disable vue/no-unused-components */
import { AgGridVue } from "ag-grid-vue";
import Formats from "@/mixins/Formats.js";
import axios from "axios";
import AgBadge from "@/components/AgGrid/AgBadge.vue";
import BaseLoader from "@/components/BaseLoader.vue";
import warehouses from "@/data/warehouses.json";
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import CustomerAddress from "@/components/AgGrid/CustomerAddress.vue";
import AgGrid from "@/mixins/AgGrid.js";
import FilterDate from "@/components/AgGrid/Filters/FilterDate.vue";

export default {
  mixins: [Formats, AgGrid],
  components: {
    AgGridVue,
    AgBadge,
    BaseLoader,
    CustomerAddress,
    agDateInput: FilterDate,
  },
  data() {
    return {
      salesPersonOptions: null,
      agInvoices: {
        columnDefs: [
          { 
            field: "DocNum", 
            headerName: "# Factura",
            width: 90,
            filter: "agTextColumnFilter",
						cellClass: "ag-clickable-cell text-blue font-weight-bold",
          },
          {
            field: "customer", 
            headerName: "Cliente", 
            width: 300,
            filter: "agTextColumnFilter",
            cellRenderer: function ({data}) {
              return `
                <span>
                  <span class="font-weight-bold">${data.CardName}</span>
                  <span class="font-weight-normal">${data.CardCode}</span>
                </span>
              `;
            }
          },
          { 
            field: "DocDate", 
            headerName: "Fecha",
            width: 80,
            valueFormatter: ({value}) => this.dateFormat(value),
            filter: "agDateColumnFilter",
          },
          { 
            field: "DocDueDate", 
            headerName: "Vencimiento",
            width: 100,
            valueFormatter: ({value}) => this.dateFormat(value),
            filter: "agDateColumnFilter",
          },
          { 
            field: "U_Warehouse", 
            headerName: "Almacén",
            width: 80,
            filter: "agSetColumnFilter",
            filterParams: {
              values: warehouses,
              defaultToNothingSelected: true,
              applyMiniFilterWhileTyping: true,
            },
          },
          { 
            field: "SlpCode", 
            headerName: "Vendedor",
            width: 120,
            filter: "agSetColumnFilter",
            filterParams: {
              values: (params) => {
                if(this.salesPersonOptions){
                  params.success(this.salesPersonOptions.map((x) => x.SalesEmployeeCode));
                } else {
                  this.watcherSP = this.$watch(() => this.salesPersonOptions, () => {
                    params.success(this.salesPersonOptions.map((x) => x.SalesEmployeeCode));
                    this.watcherSP(); // Remove watcher
                  })
                }
              },
              defaultToNothingSelected: true,
              valueFormatter: ({value}) => this.formatSalesPerson(value),
              applyMiniFilterWhileTyping: true,
            },
            valueFormatter: ({value}) => this.formatSalesPerson(value),
          },
          {
            field: "total",
            headerName: "Total",
            width: 120,
            valueFormatter: ({data}) => this.currencyFormat(data.total, data.DocCur),
            headerClass: "ag-right-aligned-header",
            type: "rightAligned",
            filter: "agNumberColumnFilter",
          },
          {
            field: "status",
            headerName: "Estatus",
            width: 80,
            cellClass: "d-flex justify-content-center",
            filter: "agSetColumnFilter",
            filterParams: {
              values: ["Abierta", "Cerrada", "Cancelada"],
              defaultToNothingSelected: true,
              applyMiniFilterWhileTyping: true,
            },
            cellRendererSelector: ({value}) => {
              const colors = {
                "Abierta": "#5e72e4",
                "Cerrada": "#adb5bd",
                "Cancelada": "#f5365c",
              };
              return {
                component: "AgBadge",
                params: {
                  text: value,
                  color: colors[value] || "#adb5bd",
                },
              };
            },
          },
          {
            field: "Comments",
            headerName: "Comentarios",
            filter: "agTextColumnFilter",
            width: 600,
          },
          {
            field: "Address2", 
            headerName: "Dirección", 
            width: 600,
            filter: "agTextColumnFilter",
            cellRenderer: function ({data}) {
              return `
                <span>
                  <span class="font-weight-bold">${data.ShipToCode}</span>
                  <span class="font-weight-normal">${data.Address2}</span>
                </span>
              `;
            }
          },
        ],
        defaultColDef: {
          menuTabs: ["filterMenuTab"],
          // suppressHeaderMenuButton: true,
          resizable: true,
          cellDataType: false,
          floatingFilter: true,
          suppressFloatingFilterButton: true,

        },
        getRowId: ({data}) => data.DocNum,
        rowModelType: "serverSide",
        cacheBlockSize: 30,
        maxBlocksInCache: 10,
        pagination: true,
        paginationPageSize: 1000,
        paginationPageSizeSelector: [100, 500, 1000, 2000],
        loadingOverlayComponent: "BaseLoader",
        suppressCellFocus: true,
        enableCellTextSelection: true,
        localeText: AG_GRID_LOCALE_ES,
        alwaysShowVerticalScroll: true,
        rowSelection: "single",
      },
    };
  },
  created() {
    this.HANA_URL = process.env.VUE_APP_HANA_URL;
    this.DATABASE = process.env.VUE_APP_DATABASE;
  },
  methods: {
    onGridReady({ api }) {
      api.setGridOption("serverSideDatasource", this.getServerSideDatasource());
      this.getSalesPerson(api);
    },
    getSalesPerson(api){
      axios
      .get(`/api/sales_persons`)
      .then((response) => {
        this.salesPersonOptions = response.data.value;
        api.refreshCells({columns: ['SlpCode']});
      })
      .catch((error) => {
        console.error(error);
      });
    },
    getServerSideDatasource() {
      return {
        getRows: (params) => {
          const { filterModel, endRow, startRow, sortModel } = params.request;

          //SUBQUERY
          const subQuery = `
            SELECT    
              "DocNum",
              "CardName",
              "CardCode",
              "DocDate",
              "DocDueDate",
              "SlpCode",
              "U_Warehouse",
              "ShipToCode",
              "Address2",
              "Comments",
              "DocCur",
              "U_Delivered",
              ("CardCode" || ' ' || "CardName") AS "customer",
              CASE WHEN "DocCur" = 'MXP' THEN "DocTotal" ELSE "DocTotalFC" END AS "total",
              CASE WHEN "CANCELED" != 'N' THEN 'Cancelada'
              ELSE
                CASE
                  WHEN "DocStatus" = 'O' THEN 'Abierta'
                  ELSE 'Cerrada'
                END
              END AS "status"

            FROM ${this.DATABASE}."OINV"
          `;

          //FILTERS
          let filters = `WHERE "U_Delivered" = 'Y'`;
          const filterModelKeys = Object.keys(filterModel);
          if (filterModelKeys.length) {
            const all = filterModelKeys.reduce((acc, key) => {
              if (filterModel[key].operator) {
                const conditionsArray = filterModel[key].conditions.map((condition) => {
                  return this.formatCondition(condition, key);
                }
              );
              const toPush = "(" + conditionsArray.join(" " + filterModel[key].operator + " ") + ")";
              acc.push(toPush);
              } else {
                const condition = this.formatCondition(filterModel[key], key);
                if(condition){
                  acc.push(condition);
                }
              }
              return acc;
            }, []);
            if(all.length){
              filters += " AND " + all.join(" AND ");
            }
          }

          //PAGINATION
          const pageSize = endRow - startRow;
          const pagination = `LIMIT ${pageSize} OFFSET ${startRow}`;

          //SORTING
          let sorting;
          if (sortModel.length) {
            sorting = `ORDER BY "${sortModel[0].colId}" ${sortModel[0].sort.toUpperCase()}`;
            if(sortModel[0].colId !== "DocNum"){
              sorting += `, "DocNum" DESC`;
            }
          } else {
            sorting = `ORDER BY "DocNum" DESC`;
          }

          //JSON PAYLOADS
          const rowDataJson = {
            query: `
              SELECT *
              FROM (${subQuery})
              ${filters}
              ${sorting}
              ${pagination}
            `
          };
          const totalsJson = {
            query: `
              SELECT COUNT(*) AS "rowCount"
              FROM (${subQuery})
              ${filters}
            `
          };

          // console.log("🟦rowDataJson", rowDataJson.query);
          // console.log("🟦totalsJson", totalsJson.query);
          
          axios
            .all([
              axios.post(`${this.HANA_URL}/api/hana/freedom`, rowDataJson),
              axios.post(`${this.HANA_URL}/api/hana/freedom`, totalsJson),
            ])
            .then(
              axios.spread((rowDataRes, totalsRes) => {
                // console.log("🟪 DATA", rowDataRes.data);
                // console.log("🟪 TOTALS", totalsRes.data);
                params.success({
                  rowData: rowDataRes.data,
                  rowCount: totalsRes.data[0].rowCount,
                });
                
              })
            )
            .catch((err) => {
              console.error(err);
              params.fail();
            });
        },
      };
    },
    formatSalesPerson(value){
      if(!this.salesPersonOptions) return value;
      const salesPerson = this.salesPersonOptions.find((x) => x.SalesEmployeeCode === value);
      return salesPerson ? salesPerson.SalesEmployeeName : value ;
    },
    onRowDoubleClicked({data}) {
      this.$router.push("/invoice/" + data.DocNum);
    },
    onCellClicked({data, colDef}) {
      if (colDef.field === "DocNum") {
        this.$router.push("/invoice/" + data.DocNum);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ag-clickable-cell {
  cursor: pointer !important;
}
.ag-clickable-cell:hover {
  background: #e9ecef !important;
}
</style>
